import { type CSSProperties, useMemo } from "react";
import { useColorScheme, useTheme } from "@mui/material";
import type { RunStatus } from "src/queries/pipelines/pipelineRuns.types";
import { RUN_STATUSES } from "../../RunsStatusBars.constants";

export const useRunStatusStyle = (
  status: RunStatus,
  style?: CSSProperties,
  backgroundBrightness: number = 0
) => {
  const theme = useTheme();
  const { lighten } = theme;
  const { palette } = theme.vars || theme;
  const { mode } = useColorScheme();

  return useMemo(() => {
    let combinedStyle: CSSProperties = {};

    if (!status) {
      return combinedStyle;
    }

    const upperStatus = status.toUpperCase();

    const statusType = RUN_STATUSES[upperStatus as keyof typeof RUN_STATUSES];

    const isUnknown = statusType === RUN_STATUSES.UNKNOWN;

    const isPreempted = statusType === RUN_STATUSES.PREEMPTED;

    const isCompletedWithRetries =
      statusType === RUN_STATUSES.COMPLETED_WITH_RETRIES ||
      statusType === RUN_STATUSES.WITH_RETRIES;

    const isCompletedWithErrors = statusType === RUN_STATUSES.COMPLETED_WITH_ERRORS;

    const isFailedWithErrors = statusType === RUN_STATUSES.FAILED_WITH_ERRORS;

    const color: string =
      palette.runStatus[statusType as keyof typeof palette.runStatus] ||
      (mode === "light" ? palette.neutral.light : palette.neutral.dark);

    const withoutBackground =
      isPreempted ||
      isCompletedWithRetries ||
      isCompletedWithErrors ||
      isFailedWithErrors;

    combinedStyle = {
      backgroundColor: withoutBackground
        ? undefined
        : lighten(color, isUnknown ? 0 : backgroundBrightness),
    } as CSSProperties;

    if (isPreempted) {
      combinedStyle.boxShadow = `0 0 0 1px ${color} inset`;
    }

    if (isCompletedWithRetries) {
      combinedStyle.border = `2px dotted ${color}`;
      combinedStyle.boxSizing = "border-box";
    }

    if (isCompletedWithErrors) {
      combinedStyle.border = `2px dotted ${color}`;
      combinedStyle.boxSizing = "border-box";
    }

    if (isFailedWithErrors) {
      combinedStyle.border = `2px dotted ${color}`;
      combinedStyle.boxSizing = "border-box";
    }

    return { ...style, ...combinedStyle };
  }, [status, palette, style, backgroundBrightness]);
};
